import React, { useEffect, useCallback } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useCustomerAPI } from "@src/api/customer_api";
import { Customer } from "@src/model/customer";
import Alert, { AlertProps } from "@src/common/alert";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

interface CustomerIndexProps { }

const CustomerIndex: React.FC<CustomerIndexProps> = (props) => {
    const [data, setData] = React.useState<Customer[]>([]);
    const [allCustomers, setAllCustomers] = React.useState<Customer[]>([]);
    const [search, setSearch] = React.useState("");
    const [hideInactive, setHideInactive] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const customerAPI = useCustomerAPI();
    const navigate = useNavigate();

    const columns = [
        {
            name: "Email",
            selector: (row: Customer) => row.email || "",
            sortable: true,
            wrap: true,
            width: "200px",
        },
        {
            name: "Organization",
            selector: (row: Customer) => row.organization_name || "N/A",
            sortable: true,
            wrap: true,
            width: "180px",
        },
        {
            name: "Org Type",
            selector: (row: Customer) => row.organization_type || "",
            sortable: true,
            cell: (row: Customer) => (
                <span className="capitalize">
                    {row.organization_type || "N/A"}
                </span>
            ),
            width: "120px",
        },
        {
            name: "Stripe Customer ID",
            selector: (row: Customer) => row.stripe_customer_id || "",
            sortable: true,
            wrap: true,
            width: "150px",
        },
        {
            name: "Product ID",
            selector: (row: Customer) => row.stripe_product_id || "N/A",
            sortable: true,
            wrap: true,
            width: "150px",
        },
        {
            name: "Product Status",
            cell: (row: Customer) => (
                <span className={`px-2 py-1 rounded text-white text-xs ${row.stripe_product_is_active ? "bg-green-500" : "bg-red-500"}`}>
                    {row.stripe_product_is_active ? "Active" : "Inactive"}
                </span>
            ),
            sortable: true,
            width: "130px",
        },
        {
            name: "Created At",
            selector: (row: Customer) => row.created_at ? new Date(row.created_at).toLocaleDateString() : "",
            sortable: true,
            width: "120px",
        },
        {
            name: "Actions",
            cell: (row: Customer) => (
                row.user_id ? (
                    <button
                        onClick={() => navigate(`/users/edit/${row.user_id}`)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-xs"
                    >
                        View User
                    </button>
                ) : (
                    <span className="text-gray-400 text-xs">No User</span>
                )
            ),
            width: "120px",
        },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetchCustomers = useCallback(
        _.debounce((searchTerm: string) => {
            setLoading(true);
            customerAPI
                .getCustomers(searchTerm, 10, 0)
                .then((response) => {
                    setAllCustomers(response);
                    setLoading(false);
                })
                .catch((error) => {
                    setStatus({ type: "error", message: "Failed to load customers: " + error.message });
                    setLoading(false);
                });
        }, 500),
        [customerAPI]
    );

    useEffect(() => {
        const filteredData = hideInactive ? allCustomers.filter(customer => !customer.stripe_product_is_active) : allCustomers;
        setData(filteredData);
    }, [hideInactive, allCustomers]);

    useEffect(() => {
        debouncedFetchCustomers(search);
        return () => {
            debouncedFetchCustomers.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <AppContainer>
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center gap-2 mb-6">
                    <h1 className="text-2xl font-bold">Customers</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">As of org user's last login.</p>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}

                <form className="w-full mx-auto mt-2 mb-4">
                    <label
                        htmlFor="default-search"
                        className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
                    >
                        Search
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg
                                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                />
                            </svg>
                        </div>
                        <input
                            type="search"
                            id="default-search"
                            onChange={(val) => setSearch(val.target.value)}
                            className="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="Search customers by email, organization..."
                            required
                        />
                    </div>
                    <div className="w-full flex justify-end gap-2 mt-2 items-center">
                        <input
                            id="hide-inactive"
                            type="checkbox"
                            checked={hideInactive}
                            onChange={(e) => setHideInactive(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label
                            htmlFor="hide-inactive"
                            className="text-sm font-medium text-gray-900 dark:text-gray-300"
                        >
                            Hide inactive products
                        </label>
                    </div>
                </form>

                <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    progressPending={loading}
                    progressComponent={<div className="py-6">Loading customers...</div>}
                    noDataComponent={<div className="py-6">No customers found</div>}
                />
            </div>
        </AppContainer>
    );
};

export default CustomerIndex;

